import React, { useState, useMemo } from 'react';
import ToolLayout from '../components/ToolLayout';

interface LanguageStats {
  language: string;
  confidence: number;
  features: string[];
}

interface ReadabilityStats {
  fleschKincaidGrade: number;
  fleschReadingEase: number;
  averageWordsPerSentence: number;
  averageSyllablesPerWord: number;
  readingLevel: string;
  readingTime: {
    slow: number;
    average: number;
    fast: number;
  };
}

interface TextStats {
  characters: {
    total: number;
    withSpaces: number;
    withoutSpaces: number;
    alphabetic: number;
    numeric: number;
    special: number;
  };
  words: {
    total: number;
    unique: number;
    averageLength: number;
    longest: string;
    shortest: string;
  };
  sentences: {
    total: number;
    averageLength: number;
    longest: string;
    shortest: string;
  };
  paragraphs: {
    total: number;
    averageLength: number;
  };
  lines: {
    total: number;
    empty: number;
    withContent: number;
  };
}

const TextStatistics: React.FC = () => {
  const [input, setInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Text statistics calculation
  const textStats = useMemo((): TextStats => {
    if (!input) {
      return {
        characters: { total: 0, withSpaces: 0, withoutSpaces: 0, alphabetic: 0, numeric: 0, special: 0 },
        words: { total: 0, unique: 0, averageLength: 0, longest: '', shortest: '' },
        sentences: { total: 0, averageLength: 0, longest: '', shortest: '' },
        paragraphs: { total: 0, averageLength: 0 },
        lines: { total: 0, empty: 0, withContent: 0 }
      };
    }

    const text = input;
    
    // Character statistics
    const totalChars = text.length;
    const withoutSpaces = text.replace(/\s/g, '').length;
    const alphabetic = (text.match(/[a-zA-ZÀ-ÿ]/g) || []).length;
    const numeric = (text.match(/[0-9]/g) || []).length;
    const special = totalChars - withoutSpaces - (text.match(/\s/g) || []).length;

    // Word statistics
    const words = text.match(/\b\w+\b/g) || [];
    const uniqueWords = new Set(words.map(w => w.toLowerCase()));
    const wordLengths = words.map(w => w.length);
    const averageWordLength = wordLengths.length > 0 ? wordLengths.reduce((a, b) => a + b, 0) / wordLengths.length : 0;
    let longestWord = '';
    let shortestWord = '';
    if (words.length > 0) {
      longestWord = words[0]!;
      shortestWord = words[0]!;
      for (const word of words) {
        if (word.length > longestWord.length) {
          longestWord = word;
        }
        if (word.length < shortestWord.length) {
          shortestWord = word;
        }
      }
    }

    // Sentence statistics
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const sentenceLengths = sentences.map(s => s.trim().split(/\s+/).length);
    const averageSentenceLength = sentenceLengths.length > 0 ? sentenceLengths.reduce((a, b) => a + b, 0) / sentenceLengths.length : 0;
    const longestSentence = sentences.reduce((a, b) => a.length > b.length ? a : b, '');
    const shortestSentence = sentences.reduce((a, b) => a.length < b.length ? a : b, longestSentence);

    // Paragraph statistics
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
    const paragraphLengths = paragraphs.map(p => (p.match(/\b\w+\b/g) || []).length);
    const averageParagraphLength = paragraphLengths.length > 0 ? paragraphLengths.reduce((a, b) => a + b, 0) / paragraphLengths.length : 0;

    // Line statistics
    const lines = text.split('\n');
    const emptyLines = lines.filter(line => line.trim().length === 0).length;
    const contentLines = lines.length - emptyLines;

    return {
      characters: {
        total: totalChars,
        withSpaces: totalChars,
        withoutSpaces: withoutSpaces,
        alphabetic: alphabetic,
        numeric: numeric,
        special: special
      },
      words: {
        total: words.length,
        unique: uniqueWords.size,
        averageLength: averageWordLength,
        longest: longestWord,
        shortest: shortestWord
      },
      sentences: {
        total: sentences.length,
        averageLength: averageSentenceLength,
        longest: longestSentence.trim(),
        shortest: shortestSentence.trim()
      },
      paragraphs: {
        total: paragraphs.length,
        averageLength: averageParagraphLength
      },
      lines: {
        total: lines.length,
        empty: emptyLines,
        withContent: contentLines
      }
    };
  }, [input]);

  // Language detection (simplified)
  const detectLanguage = useMemo((): LanguageStats => {
    if (!input || input.length < 20) {
      return { language: 'Không xác định', confidence: 0, features: [] };
    }

    const text = input.toLowerCase();
    const features: string[] = [];
    
    // Vietnamese detection
    const vietnameseChars = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/g;
    const vietnameseMatches = (text.match(vietnameseChars) || []).length;
    const vietnameseWords = ['và', 'của', 'các', 'trong', 'với', 'để', 'có', 'được', 'một', 'là'].filter(word => text.includes(word)).length;
    
    if (vietnameseMatches > 0 || vietnameseWords > 2) {
      features.push(`${vietnameseMatches} ký tự có dấu`, `${vietnameseWords} từ tiếng Việt`);
      return { language: 'Tiếng Việt', confidence: Math.min(95, vietnameseMatches * 10 + vietnameseWords * 15), features };
    }

    // English detection
    const englishWords = ['the', 'and', 'is', 'in', 'to', 'of', 'a', 'that', 'it', 'with', 'for', 'as', 'was', 'on'].filter(word => text.includes(` ${word} `)).length;
    if (englishWords > 2) {
      features.push(`${englishWords} từ tiếng Anh phổ biến`);
      return { language: 'Tiếng Anh', confidence: Math.min(90, englishWords * 12), features };
    }

    // Programming language detection
    const codePatterns = [
      { pattern: /function\s+\w+\s*\(/, lang: 'JavaScript/TypeScript' },
      { pattern: /def\s+\w+\s*\(/, lang: 'Python' },
      { pattern: /public\s+class\s+\w+/, lang: 'Java' },
      { pattern: /#include\s*</, lang: 'C/C++' },
      { pattern: /import\s+\w+/, lang: 'Java/Python' },
      { pattern: /const\s+\w+\s*=/, lang: 'JavaScript/TypeScript' }
    ];

    for (const { pattern, lang } of codePatterns) {
      if (pattern.test(text)) {
        features.push(`Cú pháp ${lang}`);
        return { language: `Mã nguồn (${lang})`, confidence: 80, features };
      }
    }

    return { language: 'Không xác định', confidence: 0, features: ['Không đủ dữ liệu để phân tích'] };
  }, [input]);

  // Readability analysis (simplified Flesch-Kincaid)
  const readabilityStats = useMemo((): ReadabilityStats => {
    if (textStats.words.total === 0 || textStats.sentences.total === 0) {
      return {
        fleschKincaidGrade: 0,
        fleschReadingEase: 0,
        averageWordsPerSentence: 0,
        averageSyllablesPerWord: 0,
        readingLevel: 'Không xác định',
        readingTime: { slow: 0, average: 0, fast: 0 }
      };
    }

    const averageWordsPerSentence = textStats.words.total / textStats.sentences.total;
    
    // Simple syllable counting (approximation)
    const countSyllables = (word: string): number => {
      return Math.max(1, (word.match(/[aeiouyàáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹ]/gi) || []).length);
    };

    const words = input.match(/\b\w+\b/g) || [];
    let totalSyllables = 0;
    for (const word of words) {
      totalSyllables += countSyllables(word);
    }
    const averageSyllablesPerWord = totalSyllables / words.length;

    // Flesch Reading Ease (modified for Vietnamese)
    const fleschReadingEase = 206.835 - (1.015 * averageWordsPerSentence) - (84.6 * averageSyllablesPerWord);
    
    // Flesch-Kincaid Grade Level
    const fleschKincaidGrade = (0.39 * averageWordsPerSentence) + (11.8 * averageSyllablesPerWord) - 15.59;

    let readingLevel = 'Rất khó';
    if (fleschReadingEase >= 90) {
      readingLevel = 'Rất dễ';
    } else if (fleschReadingEase >= 80) {
      readingLevel = 'Dễ';
    } else if (fleschReadingEase >= 70) {
      readingLevel = 'Tương đối dễ';
    } else if (fleschReadingEase >= 60) {
      readingLevel = 'Bình thường';
    } else if (fleschReadingEase >= 50) {
      readingLevel = 'Tương đối khó';
    } else if (fleschReadingEase >= 30) {
      readingLevel = 'Khó';
    }

    // Reading time estimation (words per minute)
    const slowWPM = 150;
    const averageWPM = 250;
    const fastWPM = 400;

    const readingTime = {
      slow: Math.ceil(textStats.words.total / slowWPM),
      average: Math.ceil(textStats.words.total / averageWPM),
      fast: Math.ceil(textStats.words.total / fastWPM)
    };

    return {
      fleschKincaidGrade: Math.max(0, fleschKincaidGrade),
      fleschReadingEase: Math.max(0, Math.min(100, fleschReadingEase)),
      averageWordsPerSentence,
      averageSyllablesPerWord,
      readingLevel,
      readingTime
    };
  }, [textStats, input]);

  const wordFrequency = useMemo(() => {
    if (!input) {
      return [];
    }
    
    const words = input.toLowerCase().match(/\b\w+\b/g) || [];
    const frequency: Record<string, number> = {};
    
    words.forEach(word => {
      if (word.length > 2) { // Ignore very short words
        frequency[word] = (frequency[word] || 0) + 1;
      }
    });

    return Object.entries(frequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([word, count]) => ({ word, count, percentage: (count / words.length * 100).toFixed(1) }));
  }, [input]);

  const loadSampleData = () => {
    setInput(`Xin chào! Đây là một đoạn văn bản mẫu để kiểm tra các tính năng thống kê văn bản.

Công cụ này sẽ phân tích nhiều khía cạnh khác nhau của văn bản bao gồm:
- Số lượng ký tự, từ, câu và đoạn văn
- Độ dài trung bình của các thành phần
- Tần suất xuất hiện của từ
- Ước tính thời gian đọc
- Mức độ dễ đọc của văn bản

Bạn có thể sử dụng công cụ này để tối ưu hóa nội dung viết, phân tích văn bản hoặc đơn giản là để hiểu rõ hơn về cấu trúc của một đoạn text nào đó.

Hãy thử paste đoạn văn bản của bạn vào đây và khám phá những thông tin thú vị!`);
  };

  const clearInput = () => {
    setInput('');
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(num));
  };

  const formatDecimal = (num: number, decimals: number = 1): string => {
    return num.toFixed(decimals);
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 1) {
      return 'Dưới 1 phút';
    }
    if (minutes < 60) {
      return `${minutes} phút`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}p`;
  };

  return (
    <ToolLayout
      title="Text Statistics"
      description="Thống kê chi tiết về văn bản: đếm từ/ký tự/dòng, ước tính thời gian đọc, phát hiện ngôn ngữ"
    >
      <div className="text-statistics">
        {/* Input Section */}
        <div className="input-section">
          <div className="input-header">
            <label className="input-label">Nhập văn bản cần phân tích:</label>
            <div className="input-actions">
              <button className="btn btn-sm btn-secondary" onClick={clearInput}>
                Xóa
              </button>
              <button className="btn btn-sm btn-primary" onClick={loadSampleData}>
                Dữ liệu mẫu
              </button>
            </div>
          </div>
          <textarea
            className="input-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste hoặc nhập văn bản của bạn vào đây để bắt đầu phân tích..."
            rows={8}
          />
        </div>

        {input && (
          <>
            {/* Basic Statistics */}
            <div className="stats-section">
              <h3>📊 Thống kê cơ bản</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">🔤</div>
                  <div className="stat-content">
                    <div className="stat-title">Ký tự</div>
                    <div className="stat-value">{formatNumber(textStats.characters.total)}</div>
                    <div className="stat-detail">
                      {formatNumber(textStats.characters.withoutSpaces)} (không khoảng trắng)
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📝</div>
                  <div className="stat-content">
                    <div className="stat-title">Từ</div>
                    <div className="stat-value">{formatNumber(textStats.words.total)}</div>
                    <div className="stat-detail">
                      {formatNumber(textStats.words.unique)} từ duy nhất
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">✏️</div>
                  <div className="stat-content">
                    <div className="stat-title">Câu</div>
                    <div className="stat-value">{formatNumber(textStats.sentences.total)}</div>
                    <div className="stat-detail">
                      Trung bình {formatDecimal(textStats.sentences.averageLength)} từ/câu
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📄</div>
                  <div className="stat-content">
                    <div className="stat-title">Đoạn văn</div>
                    <div className="stat-value">{formatNumber(textStats.paragraphs.total)}</div>
                    <div className="stat-detail">
                      Trung bình {formatDecimal(textStats.paragraphs.averageLength)} từ/đoạn
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📏</div>
                  <div className="stat-content">
                    <div className="stat-title">Dòng</div>
                    <div className="stat-value">{formatNumber(textStats.lines.total)}</div>
                    <div className="stat-detail">
                      {formatNumber(textStats.lines.withContent)} có nội dung
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">⏱️</div>
                  <div className="stat-content">
                    <div className="stat-title">Thời gian đọc</div>
                    <div className="stat-value">{formatTime(readabilityStats.readingTime.average)}</div>
                    <div className="stat-detail">
                      {formatTime(readabilityStats.readingTime.slow)} - {formatTime(readabilityStats.readingTime.fast)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Language Detection */}
            <div className="stats-section">
              <h3>🌐 Phát hiện ngôn ngữ</h3>
              <div className="language-card">
                <div className="language-main">
                  <span className="language-name">{detectLanguage.language}</span>
                  {detectLanguage.confidence > 0 && (
                    <span className="language-confidence">
                      Độ tin cậy: {detectLanguage.confidence}%
                    </span>
                  )}
                </div>
                {detectLanguage.features.length > 0 && (
                  <div className="language-features">
                    <strong>Đặc điểm:</strong> {detectLanguage.features.join(', ')}
                  </div>
                )}
              </div>
            </div>

            {/* Reading Analysis */}
            {textStats.words.total > 10 && (
              <div className="stats-section">
                <h3>📖 Phân tích độ dễ đọc</h3>
                <div className="readability-grid">
                  <div className="readability-card">
                    <div className="readability-title">Mức độ dễ đọc</div>
                    <div className="readability-value">{readabilityStats.readingLevel}</div>
                    <div className="readability-score">
                      Điểm Flesch: {formatDecimal(readabilityStats.fleschReadingEase, 0)}/100
                    </div>
                  </div>
                  <div className="readability-card">
                    <div className="readability-title">Cấp độ giáo dục</div>
                    <div className="readability-value">Lớp {formatDecimal(readabilityStats.fleschKincaidGrade, 0)}</div>
                    <div className="readability-score">
                      Theo thang Flesch-Kincaid
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Word Frequency */}
            {wordFrequency.length > 0 && (
              <div className="stats-section">
                <h3>🔤 Từ xuất hiện nhiều nhất</h3>
                <div className="frequency-list">
                  {wordFrequency.map((item, index) => (
                    <div key={item.word} className="frequency-item">
                      <span className="frequency-rank">#{index + 1}</span>
                      <span className="frequency-word">{item.word}</span>
                      <span className="frequency-count">{item.count} lần</span>
                      <span className="frequency-percentage">{item.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Advanced Statistics */}
            <div className="stats-section">
              <div className="section-header">
                <h3>🔬 Thống kê chi tiết</h3>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? 'Ẩn' : 'Hiện'} chi tiết
                </button>
              </div>
              
              {showAdvanced && (
                <div className="advanced-stats">
                  <div className="advanced-grid">
                    <div className="advanced-section">
                      <h4>Ký tự</h4>
                      <ul>
                        <li>Tổng cộng: {formatNumber(textStats.characters.total)}</li>
                        <li>Chữ cái: {formatNumber(textStats.characters.alphabetic)}</li>
                        <li>Số: {formatNumber(textStats.characters.numeric)}</li>
                        <li>Ký tự đặc biệt: {formatNumber(textStats.characters.special)}</li>
                      </ul>
                    </div>

                    <div className="advanced-section">
                      <h4>Từ ngữ</h4>
                      <ul>
                        <li>Độ dài trung bình: {formatDecimal(textStats.words.averageLength)} ký tự</li>
                        <li>Từ dài nhất: "{textStats.words.longest}" ({textStats.words.longest.length} ký tự)</li>
                        <li>Từ ngắn nhất: "{textStats.words.shortest}" ({textStats.words.shortest.length} ký tự)</li>
                        <li>Tỷ lệ từ duy nhất: {formatDecimal((textStats.words.unique / textStats.words.total) * 100)}%</li>
                      </ul>
                    </div>

                    <div className="advanced-section">
                      <h4>Câu</h4>
                      <ul>
                        <li>Từ/câu trung bình: {formatDecimal(readabilityStats.averageWordsPerSentence)}</li>
                        <li>Âm tiết/từ trung bình: {formatDecimal(readabilityStats.averageSyllablesPerWord)}</li>
                        <li>Câu dài nhất: {textStats.sentences.longest.substring(0, 50)}...</li>
                        <li>Câu ngắn nhất: {textStats.sentences.shortest}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Tips */}
        <div className="tips-section">
          <h4>💡 Mẹo sử dụng:</h4>
          <ul className="tips-list">
            <li>Công cụ hỗ trợ phân tích cả tiếng Việt và tiếng Anh</li>
            <li>Thời gian đọc được tính dựa trên tốc độ đọc trung bình</li>
            <li>Độ dễ đọc được tính theo thang Flesch-Kincaid (đã điều chỉnh)</li>
            <li>Sử dụng để tối ưu nội dung viết, blog, hoặc tài liệu</li>
            <li>Phân tích code cũng được hỗ trợ với các ngôn ngữ lập trình phổ biến</li>
          </ul>
        </div>
      </div>

      <style>{`
        .text-statistics {
          max-width: 1200px;
          margin: 0 auto;
        }

        .input-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .input-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .input-label {
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .input-actions {
          display: flex;
          gap: 0.5rem;
        }

        .input-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 0.9rem;
          line-height: 1.6;
          resize: vertical;
          min-height: 200px;
        }

        .input-textarea:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .stats-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .stats-section h3 {
          margin: 0 0 1.5rem 0;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .section-header h3 {
          margin: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .stat-card {
          background: var(--input-background);
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .stat-icon {
          font-size: 2rem;
          opacity: 0.8;
        }

        .stat-content {
          flex: 1;
        }

        .stat-title {
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin-bottom: 0.25rem;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }

        .stat-detail {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .language-card {
          background: var(--input-background);
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .language-main {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }

        .language-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .language-confidence {
          background: var(--primary-color);
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
        }

        .language-features {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .readability-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }

        .readability-card {
          background: var(--input-background);
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
          text-align: center;
        }

        .readability-title {
          font-size: 0.9rem;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        .readability-value {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .readability-score {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .frequency-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 0.5rem;
        }

        .frequency-item {
          display: grid;
          grid-template-columns: auto 1fr auto auto;
          gap: 1rem;
          padding: 0.5rem;
          background: var(--input-background);
          border-radius: 4px;
          border: 1px solid var(--border-color);
          align-items: center;
        }

        .frequency-rank {
          font-weight: 600;
          color: var(--text-secondary);
          font-size: 0.8rem;
        }

        .frequency-word {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          color: var(--text-primary);
          font-weight: 500;
        }

        .frequency-count {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .frequency-percentage {
          background: var(--primary-color);
          color: white;
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .advanced-stats {
          margin-top: 1rem;
        }

        .advanced-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .advanced-section h4 {
          margin: 0 0 0.75rem 0;
          color: var(--text-primary);
          font-size: 1rem;
        }

        .advanced-section ul {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .advanced-section li {
          padding: 0.4rem 0;
          border-bottom: 1px solid var(--border-color);
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .advanced-section li:last-child {
          border-bottom: none;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
        }

        .btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.8rem;
        }

        .btn-primary {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .btn-secondary {
          background: var(--input-background);
          color: var(--text-primary);
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .tips-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .tips-section h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .tips-list {
          margin: 0;
          padding-left: 1.5rem;
          color: var(--text-secondary);
        }

        .tips-list li {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
      `}</style>
    </ToolLayout>
  );
};

export default TextStatistics;
